import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { getUser } from 'helpers/user';
import { setCommunity } from 'helpers/community';
import ThSortable from 'components/ThSortable';
import moment from 'moment';

const Table = styled.table`
	width: 100%;
	font-size: 12px;
	line-height: 12px;

	thead {
		tr {
			border-top-color: #dee2e6;
			th {
				font-weight: normal !important;
				padding: 8px 2px;
			}
		}
	}
	tr {
		td,th {
			vertical-align: middle;
			&:nth-child(1) { width: 200px; }
			&:nth-child(3) { text-align: right; white-space: nowrap; }
			&:nth-child(5),&:nth-child(6) { width: 50px; text-align: center; }
		}
	}
`;

let axiosCancelToken = null;

export default function BankReconciliationPending() {
	const navigate = useNavigate();

	const [data, setData] = useState([]);
	const [selectedUser, setSelectedUser] = useState(getUser());
	const [sortDirection, setSortDirection] = useState('asc');
	const [sortField, setSortField] = useState('community_name');

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
		return function cleanup() {
			axiosCancelToken.cancel();
		}
	}, []);

	const loadUsers = (input, callback) => {
		axios.get('/api/manager/users/list', {
			params: {
				search: input,
				no_paginate: true,
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: 'Comunidades de ' + el.name
				};
			});
			callback(formatted);
		}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
	}

	useEffect(() => {
		let communities_ids = null;
		if (selectedUser && selectedUser.communities_permissions) {
			// Extraer los ids de comunidades del usuario donde responsible === true
			const perms = selectedUser.communities_permissions || {};
			communities_ids = Object.keys(perms)
				.filter((k) => {
					const p = perms[k];
					return p && p.responsible === true;
				})
				.map((k) => Number(k));
			// Mantener el comportamiento anterior: si no hay ids, usar [-1] para que la API no devuelva resultados
			if (communities_ids.length === 0) communities_ids = [-1];
		}

		const fetchData = async () => {
			await axios.get('/api/manager/treasury/dashboard-list', {
				params: {
					communities_ids: communities_ids && communities_ids.length > 0 ? communities_ids : null
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
				setData(response.data);
			}).catch((error) => {
				if (axios.isCancel(error)) return;
			});
		};
		fetchData();
	}, [selectedUser]);

	const sortTableClick = (field) => {
		if (!field) return;
		if (field === sortField) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};

	const sortedData = [...data].sort((a, b) => {
		let aValue, bValue;
		switch (sortField) {
			case 'community_name':
				aValue = a.community?.name?.toLowerCase() || '';
				bValue = b.community?.name?.toLowerCase() || '';
				break;
			case 'bankaccount_name':
				aValue = a.bankaccount_name?.toLowerCase() || '';
				bValue = b.bankaccount_name?.toLowerCase() || '';
				break;
			case 'balance_real':
				aValue = a.balance_real ?? 0;
				bValue = b.balance_real ?? 0;
				break;
			case 'last_reconciliation_date':
				aValue = a.last_reconciliation_date ? new Date(a.last_reconciliation_date) : new Date(0);
				bValue = b.last_reconciliation_date ? new Date(b.last_reconciliation_date) : new Date(0);
				break;
			case 'pending_bankremittances_count':
				aValue = a.pending_bankremittances_count ?? 0;
				bValue = b.pending_bankremittances_count ?? 0;
				break;
			case 'bankmovements_withoutbankreconciliation_count':
				aValue = a.bankmovements_withoutbankreconciliation_count ?? 0;
				bValue = b.bankmovements_withoutbankreconciliation_count ?? 0;
				break;
			default:
				aValue = '';
				bValue = '';
		}
		let result = 0;
		if (typeof aValue === 'string' && typeof bValue === 'string') {
			result = aValue.localeCompare(bValue);
		} else if (aValue > bValue) {
			result = 1;
		} else if (aValue < bValue) {
			result = -1;
		}
		return sortDirection === 'asc' ? result : -result;
	});

	const handleClick = (event, data) => {
		setCommunity(data.community, false, false);
	}

	return (
		<div className="card border-0 shadow-sm">
			<div className="card-header bg-white p-3">
				<div className="row">
					<div className="col-md-6 mb-2 mt-2 mb-md-0 mt-md-0">
						<h5 className="card-title mb-0 text-primary d-flex h-100 align-items-center justify-content-center">Estado de cuentas bancarias</h5>
					</div>
					<div className="col-md-6 mb-2 mt-2 mb-md-0 mt-md-0">
						<EmpoweredSelector
							load={loadUsers}
							onChange={value => setSelectedUser(value)}
							isClearable={true}
							label={selectedUser && selectedUser.name ? ('Comunidades de ' + selectedUser.name) : 'Todas las comunidades'}
							placeholder="Todas las comunidades"
							value={selectedUser?.id}
						/>
					</div>
				</div>
			</div>
			<div className="card-body">
				{ data.length > 0 &&
					<div className="row">
						<div className="col-md-12">
							<Table className="table table-sm table-bordered table-striped mb-0">
								<thead className="border-top-0">
									<tr>
										<ThSortable direction={sortDirection} active={sortField === 'community_name'} onClick={() => sortTableClick('community_name')}>Comunidad</ThSortable>
										<ThSortable direction={sortDirection} active={sortField === 'bankaccount_name'} onClick={() => sortTableClick('bankaccount_name')}>Cuenta bancaria</ThSortable>
										<ThSortable direction={sortDirection} active={sortField === 'balance_real'} onClick={() => sortTableClick('balance_real')}>Saldo real</ThSortable>
										<ThSortable direction={sortDirection} active={sortField === 'last_reconciliation_date'} onClick={() => sortTableClick('last_reconciliation_date')}>Conciliación</ThSortable>
										<ThSortable direction={sortDirection} active={sortField === 'pending_bankremittances_count'} onClick={() => sortTableClick('pending_bankremittances_count')}>Rem. pend.</ThSortable>
										<ThSortable direction={sortDirection} active={sortField === 'bankmovements_withoutbankreconciliation_count'} onClick={() => sortTableClick('bankmovements_withoutbankreconciliation_count')}>Mov. pend.</ThSortable>
									</tr>
								</thead>
								<tbody>
									{ sortedData.map((el, idx) => (
										<tr key={idx}>
											<td>{el.community?.name}</td>
											<td>{el.bankaccount_name}</td>
											<td className={el.balance_real < 0 ? 'bg-danger text-white' : ''}>{el.balance_real?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} €</td>
											<td>
												{el.last_reconciliation_date &&
													<small className="text-muted d-block">
														<div>
															{moment(el.last_reconciliation_date).format('DD/MM/YYYY')}
															{el.last_reconciliation_exists_later_movements && <>&nbsp;<i className="bi bi-circle-fill text-danger"></i></>}
														</div>
														<div className="mt-2">
															{el.last_reconciliation_user ?? 'Conciliación automática'}
														</div>
													</small>
												}
											</td>
											<td>{el.pending_bankremittances_count ?? 0}</td>
											<td>
												{/* <NavLink to={'/bank-reconciliation/' + el.bankaccount_id + '/movements?community_id=' + el.community.id} className="text-decoration-none"> */}
												<NavLink to={'/bank-reconciliation/' + el.bankaccount_id + '/movements'} onClick={(e) => handleClick(e, el)} className="text-decoration-none">
													{el.bankmovements_withoutbankreconciliation_count}&nbsp;
													<i className="bi bi-arrow-left-right"></i>
												</NavLink>
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</div>
				}
				{ data.length === 0 && 'Todo en orden'}
			</div>
		</div>
	);
}


